import { useState } from 'react'
import { useContacts } from '../hooks/useARE'
import { Users, Star, AlertTriangle, Minus, Mail, Shield } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

const segmentCfg: Record<string, { label: string; bg: string; text: string; icon: React.ElementType }> = {
  promoter:  { label: 'Promoteur',  bg: 'bg-emerald-100', text: 'text-emerald-700', icon: Star },
  neutral:   { label: 'Neutre',     bg: 'bg-paper-warm', text: 'text-ink', icon: Minus },
  detractor: { label: 'Détracteur', bg: 'bg-red-100',     text: 'text-red-700',     icon: AlertTriangle },
}

export function ContactHubPage() {
  const [segment, setSegment] = useState<string | undefined>(undefined)
  const [page, setPage] = useState(1)
  const { data, isLoading } = useContacts({ segment, page })
  const contacts = data?.data ?? []
  const total = data?.meta?.total ?? 0

  const filters = [
    { value: undefined, label: 'Tous' },
    { value: 'promoter', label: 'Promoteurs' },
    { value: 'neutral', label: 'Neutres' },
    { value: 'detractor', label: 'Détracteurs' },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-bold text-ink tracking-tight">Contacts CRM</h1>
        <p className="text-ink-muted mt-1">Contacts scorés et segmentés automatiquement</p>
      </div>

      <div className="flex items-center gap-2">
        {filters.map((f) => (
          <button
            key={f.label}
            onClick={() => { setSegment(f.value); setPage(1) }}
            className={cn(
              'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
              segment === f.value ? 'bg-brick text-white' : 'bg-paper-warm text-ink hover:bg-paper-warm'
            )}
          >
            {f.label}
          </button>
        ))}
        <span className="text-sm text-ink-faint ml-2">{total} contact(s)</span>
      </div>

      <div className="bg-white rounded-card border border-ink-border divide-y divide-ink-border/30">
        {isLoading ? (
          <div className="p-8 text-center text-ink-faint">Chargement…</div>
        ) : !contacts.length ? (
          <div className="p-8 text-center">
            <Users size={40} className="mx-auto text-ink-faint mb-3" />
            <p className="text-ink-muted">Aucun contact importé</p>
            <p className="text-sm text-ink-faint mt-1">Connectez votre CRM pour synchroniser vos contacts.</p>
          </div>
        ) : (
          contacts.map((c: any) => {
            const seg = segmentCfg[c.segment] ?? segmentCfg.neutral
            return (
              <div key={c.id} className="flex items-center gap-4 px-5 py-4 hover:bg-paper transition-colors">
                <div className="w-10 h-10 rounded-full bg-gold-light flex items-center justify-center text-brick text-sm font-semibold flex-shrink-0">
                  {(c.first_name?.[0] || c.email[0]).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink truncate">
                    {c.first_name} {c.last_name}
                    {c.do_not_contact && <Shield size={14} className="inline ml-1 text-red-400" />}
                  </p>
                  <p className="text-xs text-ink-faint truncate">{c.email}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold text-ink">
                    {c.satisfaction_score?.toFixed(0) ?? '—'}
                  </p>
                  <p className="text-[11px] text-ink-faint">/100</p>
                </div>
                <span className={cn('text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1', seg.bg, seg.text)}>
                  <seg.icon size={12} /> {seg.label}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0 text-xs text-ink-faint">
                  {c.review_requested_at && <span className="flex items-center gap-1"><Mail size={12} /> Demandé</span>}
                  {c.review_published_at && <span className="flex items-center gap-1 text-emerald-600"><Star size={12} /> Publié</span>}
                </div>
              </div>
            )
          })
        )}
      </div>

      {total > 20 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
            className="px-3 py-1.5 rounded-lg bg-paper-warm text-sm disabled:opacity-40">← Précédent</button>
          <span className="text-sm text-ink-muted">Page {page}</span>
          <button onClick={() => setPage(page + 1)} disabled={contacts.length < 20}
            className="px-3 py-1.5 rounded-lg bg-paper-warm text-sm disabled:opacity-40">Suivant →</button>
        </div>
      )}
    </div>
  )
}
